import type { FamilyScopedRow, IsoTimestamp, Uuid } from "../types/base.js";

/**
 * Felder, die auf eine Person zeigen. In der Datenbank verweisen sie auf
 * auth.users, nicht auf die Mitgliedszeile.
 */
const ACTOR_FIELDS = ["created_by", "checked_by", "started_by"] as const;

export interface AdoptionInput<T extends FamilyScopedRow> {
  rows: readonly T[];
  fromFamily: Uuid;
  toFamily: Uuid;
  fromActor: Uuid;
  toActor: Uuid;
  now: IsoTimestamp;
}

/**
 * Übernimmt Zeilen, die vor der Anmeldung lokal entstanden sind, in die echte
 * Familie.
 *
 * Ohne diesen Schritt hätte die Outbox Zeilen mit einer family_id, die es auf
 * dem Server nicht gibt. Jeder Upload würde abgewiesen und bis zum Aufgeben
 * wiederholt, und die Einkaufsliste, die jemand vor der Anmeldung getippt hat,
 * wäre still verloren.
 *
 * Reine Berechnung: der Aufrufer schreibt das Ergebnis über die Outbox.
 */
export function planAdoption<T extends FamilyScopedRow>(input: AdoptionInput<T>): T[] {
  const { rows, fromFamily, toFamily, fromActor, toActor, now } = input;
  if (fromFamily === toFamily) return [];

  return rows
    .filter((row) => row.family_id === fromFamily && row.deleted_at === null)
    .map((row) => {
      const copy = { ...row } as unknown as Record<string, unknown>;
      copy.family_id = toFamily;
      copy.updated_at = now;
      copy.client_updated_at = now;
      for (const field of ACTOR_FIELDS) {
        if (copy[field] === fromActor) copy[field] = toActor;
      }
      return copy as T;
    });
}

export interface ActorRepairInput<T extends { deleted_at: IsoTimestamp | null }> {
  rows: readonly T[];
  /**
   * Kennungen, die der Server kennt: die user_id jedes Mitglieds der Familie.
   * Alles andere in einem Personenfeld ist ein Wert, den es in auth.users
   * nicht gibt, und lässt die Zeile am Fremdschlüssel scheitern.
   */
  erlaubt: ReadonlySet<Uuid>;
  toActor: Uuid;
  now: IsoTimestamp;
}

/**
 * Ersetzt in den Personenfeldern jede Kennung, die der Server nicht kennt,
 * durch das angemeldete Konto.
 *
 * Warum es das zusätzlich zu planAdoption gibt: Die Übernahme sieht nur
 * Zeilen der lokalen Familie. Wer sich anmeldet und danach weiter Artikel
 * anlegt, bevor der Kontowechsel überall greift, erzeugt Zeilen mit der
 * richtigen family_id und einer Kennung, die es in auth.users nicht gibt. Der
 * Fremdschlüssel auf items.created_by schlägt zu, und die Zeile scheitert
 * beim Hochladen für immer. Am 19.09.2026 hingen daran zwei Einkaufslisten.
 *
 * Bewusst gegen eine Liste des Erlaubten statt gegen einen bestimmten falschen
 * Wert: Frühere Fassungen haben hier auch die Mitgliedszeile statt des Kontos
 * eingetragen. Wer nur den einen bekannten Fehlwert sucht, repariert die
 * Hälfte.
 *
 * Gibt nur zurück, was sich ändert. Reine Berechnung, der Aufrufer schreibt.
 */
export function planActorRepair<T extends { deleted_at: IsoTimestamp | null }>(input: ActorRepairInput<T>): T[] {
  const { rows, erlaubt, toActor, now } = input;
  const geaendert: T[] = [];

  for (const row of rows) {
    const felder = ACTOR_FIELDS.filter((field) => {
      const wert = (row as unknown as Record<string, unknown>)[field];
      // Leer ist erlaubt, alles Bekannte auch. Übrig bleibt, was der Server
      // nicht auflösen kann.
      return typeof wert === "string" && wert.length > 0 && !erlaubt.has(wert);
    });
    if (felder.length === 0) continue;

    const copy = { ...row } as unknown as Record<string, unknown>;
    for (const field of felder) copy[field] = toActor;
    copy.updated_at = now;
    copy.client_updated_at = now;
    geaendert.push(copy as T);
  }

  return geaendert;
}

export interface FamilyRepairInput<T extends FamilyScopedRow> {
  rows: readonly T[];
  /**
   * Familien, die der Server kennt: die aktive und jede, in der dieses Gerät
   * Mitglied ist. Alles andere ist eine lokale Familie aus der Zeit vor einer
   * Anmeldung und existiert auf dem Server nicht.
   */
  erlaubt: ReadonlySet<Uuid>;
  toFamily: Uuid;
  now: IsoTimestamp;
}

/**
 * Holt Zeilen in die aktive Familie, deren family_id der Server nicht kennt.
 *
 * Warum es das zusätzlich zu planAdoption gibt: Die Übernahme läuft einmal je
 * Familie und sieht nur genau die eine lokale Familie, die zu diesem Zeitpunkt
 * im Speicher des Browsers steht. Wer sich abmeldet, etwas tippt und sich neu
 * anmeldet, erzeugt Zeilen einer zweiten lokalen Familie; Safari löscht den
 * Speicher nicht installierter Seiten außerdem nach sieben Tagen, und danach
 * gilt eine neue lokale Kennung. In beiden Fällen bleiben Zeilen mit einer
 * family_id liegen, die es auf dem Server nie gab. Jeder Upload wird mit
 * "new row violates row-level security policy" abgewiesen, für immer.
 *
 * Wie planActorRepair bewusst gegen eine Liste des Erlaubten statt gegen eine
 * bestimmte falsche Kennung: Es gibt mehr als eine Art, zu einer fremden
 * Familie zu kommen, und wer nur die bekannte sucht, repariert die Hälfte.
 *
 * Gibt nur zurück, was sich ändert. Reine Berechnung, der Aufrufer schreibt.
 */
export function planFamilyRepair<T extends FamilyScopedRow>(input: FamilyRepairInput<T>): T[] {
  const { rows, erlaubt, toFamily, now } = input;
  if (erlaubt.size === 0) return [];

  return rows
    .filter((row) => !erlaubt.has(row.family_id))
    .map((row) => {
      const copy = { ...row } as unknown as Record<string, unknown>;
      copy.family_id = toFamily;
      copy.updated_at = now;
      copy.client_updated_at = now;
      return copy as T;
    });
}
